import { defaultFilter } from 'cmdk';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  getCountryCodeFromLanguage,
  TemplateFlagIcon,
} from '@/components/atoms/FlagEmoji/TemplateFlagIcon';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  PopoverComboboxContent,
  PopoverComboboxEmpty,
  PopoverComboboxGroup,
  PopoverComboboxInput,
  PopoverComboboxItem,
  PopoverComboboxList,
} from '@/components/ui/custom/PopoverCombobox';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { WhatsappTemplate } from '@/shared/types/broadcast';

export interface WhatsAppTemplateComboboxProps {
  templates: WhatsappTemplate[];
  selectedTemplateId?: string;
  isLoading?: boolean;
  /** Disable the trigger for reasons other than loading (e.g., read-only, no templates). */
  disabled?: boolean;
  onSelect: (templateId: string | null) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  placeholder?: string;
  /** Additional className for PopoverContent (e.g., 'z-1300'). */
  popoverClassName?: string;
  /** Placeholder text style for unselected state — defaults to 'text-foreground'. */
  placeholderClassName?: string;
  /** Called when the trigger button is clicked (e.g., for focus tracking). */
  onTriggerClick?: () => void;
}

/**
 * Shared WhatsApp template selector dropdown with flag icons.
 * Handles only the display and selection UI — form logic belongs to the consumer.
 */
export const WhatsAppTemplateCombobox = ({
  templates,
  selectedTemplateId,
  isLoading = false,
  disabled = false,
  onSelect,
  open,
  onOpenChange,
  placeholder,
  popoverClassName,
  placeholderClassName = 'text-foreground',
  onTriggerClick,
}: WhatsAppTemplateComboboxProps) => {
  const { t } = useTranslation();

  const selectedTemplate = templates.find((tpl) => tpl.id === selectedTemplateId);
  const isLoadingWithoutTemplate = isLoading && !selectedTemplate;
  const defaultPlaceholder = placeholder ?? t('common.select_template');

  return (
    <Popover modal open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline-neutral"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || isLoadingWithoutTemplate}
          className="h-10 w-full justify-between hover:bg-transparent"
          onClick={() => {
            if (!open) {
              onTriggerClick?.();
            }
          }}
        >
          {isLoadingWithoutTemplate && <Skeleton className="h-4 w-40" />}
          {!isLoadingWithoutTemplate && selectedTemplate && (
            <div className="flex items-center gap-2 font-normal text-foreground">
              <TemplateFlagIcon
                countryCode={getCountryCodeFromLanguage(selectedTemplate.language)}
              />
              <span>{selectedTemplate.name}</span>
              {selectedTemplate.status !== 'APPROVED' && (
                <Badge
                  type="secondary"
                  flavour={selectedTemplate.status === 'REJECTED' ? 'destructive' : 'warning'}
                >
                  {t(
                    `broadcasts.template_status.${selectedTemplate.status === 'REJECTED' ? 'rejected' : 'pending'}`
                  )}
                </Badge>
              )}
            </div>
          )}
          {!isLoadingWithoutTemplate && !selectedTemplate && (
            <span className={cn('font-normal', placeholderClassName)}>{defaultPlaceholder}</span>
          )}
          <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 text-foreground-muted" />
        </Button>
      </PopoverTrigger>
      <PopoverComboboxContent
        className={cn('w-max', popoverClassName)}
        commandProps={{
          shouldFilter: true,
          filter: (_value, search, keywords) => defaultFilter(keywords?.join(' ') ?? '', search),
        }}
      >
        <PopoverComboboxInput placeholder="common.search" />
        <PopoverComboboxList>
          <PopoverComboboxEmpty>
            {isLoading ? t('common.loading') : t('broadcasts.no_templates_available')}
          </PopoverComboboxEmpty>
          <PopoverComboboxGroup>
            {templates.map((template) => {
              const templateFlag = getCountryCodeFromLanguage(template.language);
              const isSelected = selectedTemplateId === template.id;
              const isApproved = template.status === 'APPROVED';
              return (
                <PopoverComboboxItem
                  key={template.id}
                  value={template.id}
                  keywords={[template.name]}
                  onSelect={() => {
                    onSelect(isSelected ? null : template.id);
                    onOpenChange(false);
                  }}
                >
                  <div className="flex flex-1 items-center gap-2">
                    <TemplateFlagIcon countryCode={templateFlag} />
                    <span className={cn(!isApproved && 'text-foreground-muted')}>
                      {template.name}
                    </span>
                    {!isApproved && (
                      <Badge
                        type="secondary"
                        flavour={template.status === 'REJECTED' ? 'destructive' : 'warning'}
                      >
                        {t(
                          `broadcasts.template_status.${template.status === 'REJECTED' ? 'rejected' : 'pending'}`
                        )}
                      </Badge>
                    )}
                  </div>
                  <CheckIcon className={cn('ml-auto h-4 w-4', !isSelected && 'invisible')} />
                </PopoverComboboxItem>
              );
            })}
          </PopoverComboboxGroup>
        </PopoverComboboxList>
      </PopoverComboboxContent>
    </Popover>
  );
};
